import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Mail, Copy, RefreshCw, UserCheck, UserX, ShieldCheck, Trash2, Users as UsersIcon, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReportsView from "@/components/sales/ReportsView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "sales" });
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [tab, setTab] = useState("team");

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users?with_stats=true&active_only=false`, { withCredentials: true });
      setUsers(r.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.post(`${API}/users`, inviteForm, { withCredentials: true });
      setInviteResult(r.data);
      setInviteForm({ email: "", name: "", role: "sales" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  const handleResend = async (userId) => {
    try {
      const r = await axios.post(`${API}/users/${userId}/resend-invite`, {}, { withCredentials: true });
      setInviteResult({ setup_url: r.data.setup_url, email_status: r.data.email_status, email: users.find(u => u.id === userId)?.email });
      toast.success("Neue Einladung erstellt");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { is_active: !u.is_active }, { withCredentials: true });
      toast.success(u.is_active ? "Deaktiviert" : "Aktiviert");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success(`Rolle: ${newRole}`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link kopiert");
  };

  const isAdmin = me?.role === "admin";
  if (!isAdmin) {
    return <div className="p-8 text-center"><p className="text-sm text-muted-foreground">Nur für Admins.</p></div>;
  }

  // Aggregate stats
  const totals = users.reduce((acc, u) => {
    const s = u.stats || {};
    acc.leads_total += s.leads_total || 0;
    acc.tasks_open += s.tasks_open || 0;
    return acc;
  }, { leads_total: 0, tasks_open: 0 });

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Admin</p>
          <h1 className="font-heading text-2xl font-black tracking-tight">Team & Einstellungen</h1>
          <p className="text-sm text-muted-foreground mt-1">Vertriebspartner, Pipeline-Konfiguration und Reports.</p>
        </div>
        {tab === "team" && (
          <Button onClick={() => setShowInvite(true)} className="rounded-sm gap-1.5" data-testid="invite-btn">
            <Plus size={14} /> Vertriebler einladen
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-1">
        {[
          { k: "team", l: "Team", icon: UsersIcon },
          { k: "reports", l: "Reports", icon: BarChart3 },
        ].map(t => {
          const I = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2 -mb-px ${tab === t.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`team-tab-${t.k}`}
            >
              <I size={13} /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "reports" && <ReportsView />}

      {tab === "team" && (
        <>
          {/* Aggregate Stats */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Team-Mitglieder" value={users.filter(u => u.is_active !== false).length} hint={`${users.filter(u => u.role === 'sales').length} Vertriebler`} />
            <StatCard label="Leads (gesamt)" value={totals.leads_total} hint={`${totals.tasks_open} offene Aufgaben`} />
          </div>

      {/* Users-Tabelle */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border px-4 py-2.5 bg-muted/30">
          <span>User</span>
          <span>Rolle</span>
          <span>Leads (offen)</span>
          <span>Gewonnen</span>
          <span></span>
        </div>
        {loading && <div className="p-6 text-sm text-muted-foreground">Laden...</div>}
        {!loading && users.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine User.</div>}
        {users.map(u => {
          const pending = !u.is_active && !u.password_hash;
          const stats = u.stats || {};
          return (
            <div
              key={u.id}
              className={`grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center px-4 py-3 border-b border-border last:border-0 ${u.is_active === false && !pending ? "bg-muted/20 opacity-60" : ""}`}
              data-testid={`user-row-${u.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center text-[11px] font-bold shrink-0">
                  {(u.name || u.email)[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{u.name || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                {pending && <span className="text-[9px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Wartet auf Setup</span>}
                {u.is_active === false && !pending && <span className="text-[9px] uppercase tracking-widest font-bold bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Inaktiv</span>}
                {u.id === me?.id && <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Du</span>}
              </div>
              <div>
                <Select value={u.role || "sales"} onValueChange={(v) => handleRoleChange(u.id, v)} disabled={u.id === me?.id}>
                  <SelectTrigger className="w-32 rounded-sm h-8 text-xs" data-testid={`role-picker-${u.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin"><span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Admin</span></SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <span className="text-sm font-mono tabular-nums">{stats.leads_open || 0}</span>
              <span className="text-sm font-mono tabular-nums text-emerald-700">{stats.leads_won || 0}</span>
              <div className="flex gap-1">
                {pending && (
                  <Button size="sm" variant="outline" onClick={() => handleResend(u.id)} className="rounded-sm h-8 gap-1.5 text-xs" data-testid={`resend-${u.id}`}>
                    <RefreshCw size={11} /> Re-send
                  </Button>
                )}
                {u.id !== me?.id && (
                  <Button size="sm" variant="ghost" onClick={() => handleToggleActive(u)} className="rounded-sm h-8 px-2" title={u.is_active === false ? "Aktivieren" : "Deaktivieren"}>
                    {u.is_active === false ? <UserCheck size={13} /> : <UserX size={13} className="text-muted-foreground hover:text-destructive" />}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
        </>
      )}

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={(o) => { setShowInvite(o); if (!o) setInviteResult(null); }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Vertriebler einladen</DialogTitle>
          </DialogHeader>
          {!inviteResult ? (
            <form onSubmit={handleInvite} className="space-y-3" data-testid="invite-form">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">E-Mail *</Label>
                <Input type="email" required value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" data-testid="invite-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
                <Input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm" data-testid="invite-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Rolle</Label>
                <Select value={inviteForm.role} onValueChange={v => setInviteForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales (Vertriebler)</SelectItem>
                    <SelectItem value="admin">Admin (sieht alles)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowInvite(false)} className="rounded-sm">Abbrechen</Button>
                <Button type="submit" disabled={saving} className="rounded-sm gap-1.5" data-testid="invite-submit"><Mail size={13} /> Einladen</Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3" data-testid="invite-result">
              {inviteResult.email_status?.sent ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm dark:bg-emerald-950/20 dark:border-emerald-900">
                  <p className="font-medium">📧 Einladungs-E-Mail wurde verschickt</p>
                  <p className="text-xs text-muted-foreground mt-1">an <code>{inviteResult.email}</code></p>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm dark:bg-amber-950/20 dark:border-amber-900">
                  <p className="font-medium">⚠️ E-Mail-Versand nicht konfiguriert</p>
                  <p className="text-xs text-muted-foreground mt-1">Schicke dem Vertriebler diesen Link manuell:</p>
                </div>
              )}
              <div className="bg-muted border border-border rounded-sm p-2 flex items-center gap-2 min-w-0">
                <code className="text-xs flex-1 min-w-0 truncate font-mono" data-testid="setup-url">{inviteResult.setup_url}</code>
                <Button size="sm" variant="outline" onClick={() => copyLink(inviteResult.setup_url)} className="rounded-sm h-8 gap-1.5" data-testid="copy-link-btn">
                  <Copy size={12} /> Kopieren
                </Button>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => { setShowInvite(false); setInviteResult(null); }} className="rounded-sm">Fertig</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, hint, mono }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">{label}</p>
      <p className={`text-2xl font-black tracking-tight ${mono ? "font-mono tabular-nums" : "font-heading"}`}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
